/**
 * Builds the GTM data layer object matching PJPP setup_data_layer behavior.
 * @see setup_data_layer in PJPP project
 */
export interface DataLayerParams {
  pathname: string;
  searchString: string;
  url: string;
  referrer: string;
  appType: "desktop_web" | "mobile_web";
  countryVersionName?: string | null;
  affiliateName?: string | null;
  affiliateLabel?: string | null;
  affiliateId?: number | string | null;
  affiliateGroupLabel?: string;
}

type JSONValue =
  | string
  | number
  | boolean
  | JSONValue[]
  | { [key: string]: JSONValue };

/**
 * General page-level data layer (no event key, no email).
 * Email and user-specific fields are pushed only inside the success event
 * via {@link pushGTMSuccessEvent} so that GTM reads them at event fire time.
 */
export function buildDataLayer(
  params: DataLayerParams
): Record<string, JSONValue> {
  const { searchString, url, referrer, appType } = params;
  const searchParams = new URLSearchParams(searchString);

  // Match PJPP: params[:area] || 'blog', params[:url] || request.referer
  const area = searchParams.get("area");
  const pageUrl = searchParams.get("url") || url;
  const referrerUrl = searchParams.get("referrer_url") || referrer;

  const dl: Record<string, JSONValue> = {
    area: area ?? "blog",
    app_type: searchParams.get("app_type") || appType,
    url: pageUrl,
    user_action: searchParams.get("user_action") ?? "",
    // Required by GTM fbq PageView (e.g. cv.toUpperCase()) - avoid undefined
    country_version_name: params.countryVersionName ?? "",
    affiliate_id: params.affiliateId ?? 0,
    affiliate_label: params.affiliateLabel ?? "",
    affiliate_name: params.affiliateName ?? "",
    affiliate_group_label: params.affiliateGroupLabel ?? "",
  };

  if (referrerUrl) {
    dl.referrer = referrerUrl;
  }

  return dl;
}

export function pushGTMPageDataLayer(params: DataLayerParams): void {
  if (typeof window === "undefined") return;

  const browserWindow = window as Record<string, unknown>;
  const dataLayer = (browserWindow.dataLayer ?? []) as Record<
    string,
    unknown
  >[];
  browserWindow.dataLayer = dataLayer;
  dataLayer.push(buildDataLayer(params));
}

export interface GTMSuccessEventParams {
  flow: string;
  email?: string;
  signupFlow?: string;
  membershipStage?: string;
  membershipType?: string;
  area?: string;
  user_action?: string;
  appType?: string;
  desireJobId?: number | string;
  saleId?: number | string;
  accountId?: number | string;
}

/**
 * Pushes a single GTM dataLayer entry that combines `event: "success"` with
 * all user data so GTM tags can read every variable at event fire time.
 */
export function pushGTMSuccessEvent(params: GTMSuccessEventParams): void {
  if (typeof window === "undefined") return;

  const w = window as Record<string, unknown>;
  const dl = (w.dataLayer ?? []) as Record<string, unknown>[];
  w.dataLayer = dl;
  const successEvent: Record<string, JSONValue> = {
    event: "success",
    email: params.email ?? "",
    signup_flow: params.signupFlow ?? "",
    membership_stage: params.membershipStage ?? "",
    membership_type: params.membershipType ?? "",
    user_action: params.user_action ?? "",
    area: params.area ?? "blog",
    app_type: params.appType ?? "",
    eventModel: {
      event_category: "onboarding",
      event_label: params.flow,
      value: params.flow,
    },
  };

  if (params.desireJobId !== undefined && params.desireJobId !== null) {
    successEvent.desire_job_id = params.desireJobId;
  }

  if (params.saleId !== undefined && params.saleId !== null) {
    successEvent.sale_id = params.saleId;
  }

  if (params.accountId !== undefined && params.accountId !== null) {
    successEvent.account_id = params.accountId;
  }

  dl.push(successEvent);
}
